import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { login, logout } from "../features/auth.slice";
import { auth } from "../firebase";
import {
  browserSessionPersistence,
  setPersistence,
  signInWithEmailAndPassword,
} from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { alertOpen, AlertValue } from "../features/alert.slice";
import { ButtonLoading } from "../components/spinner/Spiner";
import Button from "../components/button/Button";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import FormController from "../components/fields/FormController";
import { getTenantId } from "../utility/FireStoreUtility";
import jwtApi from "../api/Jwt.api";
import authApi from "../api/Auth.api";
import { SessionKey } from "../config/const";
import { requestIdCardAuth, requestManagePointAuth } from "../api/Companies.api";
import systemParametersQuery from "../firestore/SystemParameters.query";
import companiesQuery from "../firestore/Companies.query";
import { OptionContract } from "../model/OptionContract";
import { isOptionActive } from "../controller/OptionController";

export const Login = () => {
  const dispatch = useDispatch();
  const navigation = useNavigate();
  const [isResetPass, setIsRestPass] = useState(false);
  const [isload, setIsLoad] = useState(false);

  interface LoginForm {
    tenantId: string;
    requestTenantId?: string;
    requestEmail?: string;
    email: string;
    password: string;
    isResetPass?: boolean;
  }
  // バリデーションルール
  const schema = yup.object({
    tenantId: yup.string()
    .when('isResetPass', {
      is: false,
      then: yup.string().required(),
      }
    ),
    email: yup.string()
    .when('isResetPass', {
      is: false,
      then: yup.string().email().required(),
      }
    ),
    password: yup.string()
      .when('isResetPass', {
        is: false,
        then: yup.string().required()
        }
      ),
    isResetPass: yup.boolean(),
    requestEmail: yup.string()
      .when('isResetPass', {
        is: true,
        then: yup.string().email().required(),
        }
      ),
    requestTenantId: yup.string()
      .when('isResetPass', {
        is: true,
        then: yup.string().required(),
        }
      ),
  });

  const { control, handleSubmit, setValue } = useForm<LoginForm>({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    setValue('isResetPass', isResetPass);
  },[isResetPass, setValue]);

  //ログイン
  const onSubmit = async (data: LoginForm) => {
    setIsLoad(true);
    const tenantIdValue = await getTenantId(data.tenantId)
    if(!tenantIdValue.success){
      dispatch(
        alertOpen(AlertValue("ご確認ください。", "danger", "団体番号が間違っています。"))
      );
      setIsLoad(false);
      return;
    }
    auth.tenantId = tenantIdValue.data as string;
    //ここでIPアドレス制限チェック
    const result = await authApi.requestIpAddressCheck(auth.tenantId);
    if(result.success){
      setPersistence(auth, browserSessionPersistence);
      signInWithEmailAndPassword(auth, data.email, data.password)
        .then(async (userCredential) => {
          // Signed in
          if (userCredential.user?.emailVerified) {
            sessionStorage.removeItem(SessionKey.SIDEBAR_STATE);
            dispatch(login(userCredential));
            const currentUser = auth.currentUser;
            await jwtApi.requestGetJwt(currentUser?.email!, currentUser?.uid!, currentUser?.tenantId!);
            // 利用者IDカード権限の有無を取得
            const userIdCardAuth = await requestIdCardAuth(currentUser?.tenantId!);
            // ポイント管理権限の有無を取得
            const managePointAuth = await requestManagePointAuth(currentUser?.tenantId!);
            sessionStorage.setItem(SessionKey.USER_ID_CARD_AUTH, userIdCardAuth.success ? userIdCardAuth.data : false);
            sessionStorage.setItem(SessionKey.MANAGE_POINT_AUTH, managePointAuth.success ? managePointAuth.data : false);
            // マスタのオプション契約を取得
            const masterOptionList = await systemParametersQuery.getOptionContract();
            // 団体のオプション契約を取得
            const options = await companiesQuery.getOptionContractList(currentUser?.tenantId!);
            const masterOptionKeys = Object.keys(masterOptionList);
            // オプション設定で有効のものがあるか判定
            let masterOptionResult = false;
            for (let i = 0; i < masterOptionKeys.length; i++) {
              const key = masterOptionKeys[i];
              let option: OptionContract | undefined;
              option = options.find((option) => option.optionId === masterOptionList[key].optionId);
              if (masterOptionList[key].status && option && isOptionActive(option)) {
                masterOptionResult = true;
              }
            }
            sessionStorage.setItem(SessionKey.OPTION_CONTRACT_LIST, String(masterOptionResult));
            navigation("/message");
          } else {
            logout();
            setIsLoad(false);
            dispatch(
              alertOpen(
                AlertValue(
                  "登録したメールアドレスが認証されていません。メールを確認して認証してください。",
                  "warning"
                )
              )
            );
          }
          // ...
        })
        .catch((error) => {
          const errorCode = error.code;
          const errorMessage: string = "ログインに失敗しました。";
          let errorMessageInfo: string;
          if (errorCode.toString() === "auth/wrong-password") {
            errorMessageInfo = "パスワードが間違っています。";
          } else if (errorCode.toString() === "auth/user-not-found") {
            errorMessageInfo = "未登録のメールアドレスです。";
          } else if (errorCode.toString() === "auth/invalid-email") {
            errorMessageInfo = "入力したメールアドレスの形式に問題があります。";
          } else if (errorCode.toString() === "auth/user-disabled") {
            errorMessageInfo = "入力したメールアドレスは無効です。";
          } else if (errorCode.toString() === "auth/too-many-requests") {
            errorMessageInfo =
              "複数回ログインに失敗したため、一時的に無効となりました。パスワードのリセットまたは、時間をおいてログインしてください";
          } else {
            errorMessageInfo = "";
          }
          console.error(errorCode);
          dispatch(
            alertOpen(AlertValue(errorMessage, "danger", errorMessageInfo))
          );
          setIsLoad(false);
        });
      }
      else{
        logout();
        setIsLoad(false);
        dispatch(
          alertOpen(
            AlertValue(
              "サーバーに接続できないか、このIPアドレスからのログインは許可されていません。",
              "danger"
            )
          )
        );
      }
    };

  //パスワードリセット
  const submitPasswordResetEmail = async (login: LoginForm) => {
    const tenantIdValue = await getTenantId(login.requestTenantId!)
    if(!tenantIdValue.success){
      dispatch(
        alertOpen(AlertValue("ご確認ください。", "danger", "団体番号が間違っています。"))
      );
      return;
    }
    await authApi.requestTenantSendPasswordResetEmail(tenantIdValue.data!, login.requestEmail!)
      .then((resp) => {
        // メール送信成功
        dispatch(
          alertOpen(AlertValue("変更メールを送信しました。", "success"))
        );
      })
      .catch((error) => {
        // メール送信失敗
        dispatch(alertOpen(AlertValue("変更メールを送信に失敗しました", "danger")));
        console.error(error);
      });
    return;
  };
  const loginPage = () => {
    return (
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form
            className="space-y-6"
            onSubmit={handleSubmit(onSubmit)}
            method="POST"
          >
            <FormController
              control={control}
              name={"tenantId"}
              label={"団体番号"}
              type={"tenantId"}
            />
            <FormController
              control={control}
              name={"email"}
              label={"メールアドレス"}
              type={"email"}
            />
            <FormController
              control={control}
              name={"password"}
              label={"パスワード"}
              type={"password"}
            />
            <div className="flex items-center justify-between">
              <div className="text-sm">
                <p
                  className="font-medium text-indigo-600 hover:text-indigo-500 cursor-pointer"
                  onClick={() => setIsRestPass(true)}
                >
                  パスワードをお忘れですか？
                </p>
              </div>
            </div>

            <div>
              <Button submit disabled={isload} block>
                {isload ? <ButtonLoading /> : "ログイン"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const resetPassPage = () => {
    return (
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form
            className="space-y-6"
            onSubmit={handleSubmit(submitPasswordResetEmail)}
            method="POST"
          >
            <div className="mb-4">
              <label
                htmlFor="password"
                className="mb-4 text-left block text-xs font-medium text-gray-500"
              >
                団体番号とメールアドレスを入力してください
              </label>
              <FormController
                control={control}
                name={"requestTenantId"}
                label={"団体番号"}
                type={"tenantId"}
              />
              <FormController
                id={"requestEmail"}
                control={control}
                name={"requestEmail"}
                label={"メールアドレス"}
                type={"email"}
              />
            </div>

            <div>
              <button
                type="submit"
                className="pt-2 flex w-full justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                変更メールの送信
              </button>
              <div className="text-right text-sm pt-2">
                <p
                  className="cursor-pointer text-right text-indigo-600 hover:text-indigo-500 cursor-pointer"
                  onClick={() => setIsRestPass(false)}
                >
                  ログイン画面へ
                </p>
              </div>
            </div>
          </form>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="bg-[#fcfafd] flex min-h-full flex-col justify-center sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <h2 className="text-center text-3xl font-bold tracking-tight text-gray-900">
            イベントサポート
          </h2>
        </div>
        {isResetPass ? resetPassPage() : loginPage()}
      </div>
    </>
  );
};